import type { Tool } from '../../types/tools'

export const SCREENING_TOOLS: Tool[] = [
  {
    slug: 'pitch-deck-screener',
    name: 'Pitch Deck Screener',
    icon: 'presentation',
    categories: ['find', 'evaluate'],
    featured: true,
    cardDescription:
      'Check a pitch deck against your investment criteria and see what to ask before the first meeting.',
    seo: {
      title: 'AI Pitch Deck Screener for VC Deal Screening | DiligenceGPT™',
      description:
        'Screen startup pitch decks against your fund’s investment thesis. DiligenceGPT™ summarizes the business, flags missing information and drafts founder follow-up questions for analyst review.',
      keywords: ['AI pitch deck screener', 'AI pitch deck analysis', 'VC deal screening', 'investment thesis matching'],
    },
    hero: {
      supporting:
        'Assess startup pitch decks against your investment criteria, identify missing information, and prioritize your next founder meeting.',
      bullets: [
        'See how a deck lines up with your thesis',
        'Spot the information a deck leaves out',
        'Walk into founder calls with sharper questions',
      ],
    },
    whatIs: {
      question: 'What is an AI pitch deck screener?',
      paragraphs: [
        'An AI pitch deck screener reads a startup’s pitch deck and organizes what it says against the criteria an investment team already uses to decide whether to take a meeting — sector, stage, geography, business model, traction and team.',
        'DiligenceGPT™’s Pitch Deck Screener is built for VC analysts, associates and partners who review a high volume of inbound decks. You provide the deck and your fund’s screening criteria. The output is a structured screening summary: how the company appears to fit your thesis, a plain-language summary of the business, strengths and concerns tied back to what the deck actually says, the information that is missing, and questions to raise with the founder.',
        'The screener supports first-pass prioritization. It does not make investment decisions, and its fit assessment reflects only the information in the deck and the criteria you set — so analysts should review every summary before it informs a pass or a meeting.',
      ],
    },
    steps: [
      {
        title: 'Provide the deck and your criteria',
        body: 'Add the startup’s pitch deck and the screening criteria your fund applies — thesis sectors, stage, geography, check size and any must-haves.',
      },
      {
        title: 'Run the screen',
        body: 'DiligenceGPT™ reads the deck, maps its claims to each criterion and notes where the deck is silent or unclear.',
      },
      {
        title: 'Review and refine',
        body: 'Check the summary against the deck, adjust your criteria if needed, and decide whether the company merits a founder meeting.',
      },
    ],
    outputs: [
      { title: 'Investment thesis alignment', body: 'Criterion-by-criterion view of where the company appears to meet, partially meet or miss your mandate.' },
      { title: 'Business model summary', body: 'A concise description of what the company sells, to whom, and how it says it makes money.' },
      { title: 'Evidence-supported strengths and concerns', body: 'Observations linked to the slides they come from, so reviewers can check the reasoning.' },
      { title: 'Missing information', body: 'Data a typical screen needs that the deck does not provide, such as retention, burn or round terms.' },
      { title: 'Founder follow-up questions', body: 'Specific questions to close the gaps before or during the first call.' },
      { title: 'Screening summary for analyst review', body: 'A short write-up an analyst can edit and share with the team.' },
    ],
    preview: {
      kind: 'Screening scorecard',
      title: 'Thesis alignment',
      subject: 'Tallyfox · Series A · AP automation for mid-market finance teams',
      metrics: [
        { label: 'Sample fit score', value: '78 / 100', note: 'Against sample criteria' },
        { label: 'Criteria met', value: '4 of 6' },
      ],
      rows: [
        { label: 'Sector: B2B fintech', status: 'strong', detail: 'Accounts-payable workflow software for finance teams.', source: 'Slide 3' },
        { label: 'Stage: Seed – Series A', status: 'strong', detail: 'Raising a Series A.', source: 'Slide 14' },
        { label: 'Geography: North America', status: 'strong', detail: 'Customers listed in the US and Canada.', source: 'Slide 9' },
        { label: 'Recurring revenue', status: 'partial', detail: 'ARR stated; growth period not specified.', source: 'Slide 8' },
        { label: 'Net revenue retention', status: 'gap', detail: 'Not provided in the deck.' },
        { label: 'Founder-market experience', status: 'strong', detail: 'CEO previously led AP operations at a mid-market company.', source: 'Slide 12' },
      ],
      gaps: ['Net revenue retention and logo churn', 'Monthly burn and runway', 'Use of proceeds for the round'],
    },
    process: {
      heading: 'Screen with your fund’s criteria, not a generic checklist',
      body: 'Set the criteria your team already uses — thesis sectors, stage, geography, check size, ownership targets and deal-breakers — and every deck is screened against the same mandate. When a company clears the first screen, carry the work forward instead of starting over.',
      configurable: ['Thesis sectors and themes', 'Stage, geography and check size', 'Must-have and deal-breaker criteria', 'Custom scoring criteria'],
      next: { slug: 'investment-memo-writer', prompt: 'Screened a pitch deck? Continue with an investment memo.' },
    },
    faqs: [
      { question: 'What does the Pitch Deck Screener do?', answer: 'It reads a startup’s pitch deck, compares what the deck says against your investment criteria, summarizes the business, and lists missing information and founder follow-up questions in a structured screening summary.' },
      { question: 'What information do I need?', answer: 'The company’s pitch deck and your fund’s screening criteria. The more specific your criteria — sectors, stage, geography, check size, must-haves — the more useful the alignment view.' },
      { question: 'Can I use my fund’s criteria?', answer: 'Yes. DiligenceGPT™ is designed around fund-specific thesis matching, so decks are assessed against your mandate rather than a generic VC framework.' },
      { question: 'How should analysts review the results?', answer: 'Treat the summary as a first draft. Check each observation against the slide it references, confirm anything material with the founder, and use your own judgment to decide whether to take a meeting. The fit score reflects your criteria and the deck’s contents only; it is not an investment recommendation.' },
      { question: 'How does it fit into the diligence workflow?', answer: 'It sits at the top of the funnel. Companies that clear the screen can move into deeper work — data room review, market and competitor research, financial analysis and, ultimately, an investment memo.' },
    ],
    finalCta: {
      heading: 'Spend your next hour on the right founder.',
      body: 'Screen decks against your own criteria and walk into the meetings that matter with the right questions.',
    },
    related: ['investment-memo-writer', 'data-room-review', 'market-research', 'competitor-analysis', 'founder-research', 'financial-analysis', 'deal-sourcing', 'valuation-comps', 'portfolio-reporting'],
    videoId: null,
    transcript: null,
    verifiedUsageCount: null,
    illustrativeUsageCount: 640,
  },
  {
    slug: 'investment-memo-writer',
    name: 'Investment Memo Writer',
    icon: 'file-text',
    categories: ['evaluate'],
    featured: true,
    cardDescription:
      'Draft a structured investment committee memo from decks, documents and research in your fund’s format.',
    seo: {
      title: 'AI Investment Memo Writer for VC Investment Committees | DiligenceGPT™',
      description:
        'Draft investment committee memos from pitch decks, company documents and research using your fund’s template. DiligenceGPT™ organizes the evidence; your team makes the call.',
      keywords: ['AI investment memo generator', 'VC investment memo writer', 'investment committee memo', 'automated investment memo'],
    },
    hero: {
      supporting:
        'Turn pitch decks, company documents, and research into a structured investment memo using your fund’s template.',
      bullets: [
        'Start IC memos from a structured first draft',
        'Keep every claim tied to its source',
        'Follow your fund’s own memo format',
      ],
    },
    whatIs: {
      question: 'What is an AI investment memo writer?',
      paragraphs: [
        'An AI investment memo writer assembles the information gathered during diligence into the structured document an investment committee reviews — company overview, market, product, team, traction, financials, risks and the investment rationale.',
        'DiligenceGPT™’s Investment Memo Writer is for analysts and partners preparing for IC. You provide the materials you already have — the pitch deck, company documents, research notes and outputs from other DiligenceGPT™ tools — along with your fund’s memo template. It produces a first draft organized to that template, with source references so reviewers can trace each statement.',
        'The draft is a starting point, not a recommendation. The investment thesis, the judgment calls and the final recommendation belong to your team; the writer removes the assembly work so more time goes into the thinking.',
      ],
    },
    steps: [
      {
        title: 'Provide materials and your template',
        body: 'Add the deck, company documents and research you have gathered, plus your fund’s memo template or section structure.',
      },
      {
        title: 'Generate the draft',
        body: 'DiligenceGPT™ organizes the evidence into each memo section and notes where information is thin or missing.',
      },
      {
        title: 'Review and refine',
        body: 'Edit the draft, add your team’s view and investment rationale, and resolve open questions before it goes to IC.',
      },
    ],
    outputs: [
      { title: 'Template-structured draft', body: 'Sections that follow your fund’s memo format instead of a generic outline.' },
      { title: 'Company and market overview', body: 'A clear description of the business and the market it operates in, drawn from your materials.' },
      { title: 'Key risks and mitigants', body: 'Risks identified in the evidence, with space for the team’s view on each.' },
      { title: 'Source references', body: 'Pointers back to the deck slide, document or research note behind each statement.' },
      { title: 'Open diligence items', body: 'Information still needed before the memo can support a decision.' },
    ],
    preview: {
      kind: 'Investment memo draft',
      title: 'IC memo — draft for review',
      subject: 'Tallyfox · Series A · AP automation for mid-market finance teams',
      sections: [
        { heading: 'Company overview', body: 'Tallyfox automates invoice capture, approval routing and payment scheduling for finance teams at companies with 200–2,000 employees.', source: 'Deck, slides 3–5' },
        { heading: 'Market', body: 'The company positions against manual AP processes and legacy ERP add-ons. Market sizing in the deck is top-down and should be tested.', source: 'Deck, slide 6 · Market research notes' },
        { heading: 'Traction', body: 'Reported ARR and customer count are stated; the growth period and retention are not.', source: 'Deck, slide 8' },
        { heading: 'Key risks', body: 'Competitive pressure from ERP vendors; dependence on payment-partner integrations.', source: 'Competitor analysis' },
      ],
      gaps: ['Cohort retention data', 'Payment-partner contract terms', 'Partner view on investment rationale'],
    },
    process: {
      heading: 'Your memo format, your investment view',
      body: 'Use your fund’s own memo sections and ordering so drafts arrive in the format your IC expects. Pull in outputs from screening, market, competitor and financial work rather than re-writing them.',
      configurable: ['Memo template and section order', 'Required sections for IC', 'Fund-specific evaluation criteria', 'Risk and mitigant framing'],
      next: { slug: 'data-room-review', prompt: 'Drafted your memo? Close open items with a data room review.' },
    },
    faqs: [
      { question: 'What does the Investment Memo Writer do?', answer: 'It turns the materials gathered during diligence into a structured first draft of an investment memo, organized to your fund’s template with references back to the source material.' },
      { question: 'What information do I need?', answer: 'Your diligence materials — typically the pitch deck, company documents and research notes — and your fund’s memo template or preferred section structure. Outputs from other DiligenceGPT™ tools can be used as inputs.' },
      { question: 'Can I use my fund’s template?', answer: 'Yes. The draft follows the sections and order your fund uses, so it arrives in a familiar format for your investment committee.' },
      { question: 'How should analysts review the draft?', answer: 'Verify each statement against its source, add the team’s judgment and investment rationale, and resolve open diligence items before circulating. The draft does not make or imply an investment decision.' },
      { question: 'How does it fit into the diligence workflow?', answer: 'It is typically one of the last steps before IC — drawing on screening, market research, competitor analysis, data room review and financial analysis completed earlier.' },
    ],
    finalCta: {
      heading: 'Get to IC with the thinking done, not the formatting.',
      body: 'Draft memos in your fund’s format from the evidence you already have.',
    },
    related: ['pitch-deck-screener', 'data-room-review', 'financial-analysis', 'market-research', 'competitor-analysis', 'valuation-comps', 'founder-research', 'portfolio-reporting', 'deal-sourcing'],
    videoId: null,
    transcript: null,
    verifiedUsageCount: null,
    illustrativeUsageCount: 410,
  },
  {
    slug: 'data-room-review',
    name: 'Data Room Review',
    icon: 'folder-search',
    categories: ['evaluate'],
    featured: true,
    cardDescription:
      'Find answers in company documents and flag missing information or inconsistencies for follow-up.',
    seo: {
      title: 'AI Data Room Review for Due Diligence | DiligenceGPT™',
      description:
        'Review startup data rooms faster. DiligenceGPT™ helps investment teams find answers in company documents and flag missing information or inconsistencies for further diligence.',
      keywords: ['AI data room analysis', 'AI due diligence document review', 'startup data room review', 'investment document analysis'],
    },
    hero: {
      supporting:
        'Review company documents, find answers, and flag missing information or inconsistencies for further diligence.',
      bullets: [
        'Ask questions across a company’s documents',
        'Surface inconsistencies between documents',
        'Build a clear list of what is still missing',
      ],
    },
    whatIs: {
      question: 'What is AI data room review?',
      paragraphs: [
        'AI data room review applies language models to the documents a company shares during diligence — financials, contracts, cap tables, board materials, product and customer documentation — to help investors find information and check it for consistency.',
        'DiligenceGPT™’s Data Room Review is for investment teams working through a company’s data room. You provide the documents and the questions or checklist your team uses. It returns answers with references to the documents they come from, flags where figures or statements appear to conflict, and lists information your checklist expects but the data room does not contain.',
        'It speeds up reading and cross-checking; it does not replace legal, financial or technical diligence by qualified professionals. Every flagged item should be confirmed against the underlying document.',
      ],
    },
    steps: [
      {
        title: 'Provide documents and your checklist',
        body: 'Add the company’s data room documents and the diligence questions or checklist your team works from.',
      },
      {
        title: 'Run the review',
        body: 'DiligenceGPT™ reads the documents, answers your questions and checks statements and figures against one another.',
      },
      {
        title: 'Review and follow up',
        body: 'Confirm answers against the cited documents and send outstanding requests back to the company.',
      },
    ],
    outputs: [
      { title: 'Answers with document references', body: 'Responses to your diligence questions, each pointing to the document it came from.' },
      { title: 'Inconsistency flags', body: 'Places where figures or statements differ between documents.' },
      { title: 'Missing-information list', body: 'Items your checklist expects that the data room does not contain.' },
      { title: 'Document summaries', body: 'Short summaries of key documents so reviewers know where to look.' },
      { title: 'Follow-up request list', body: 'A draft list of questions and document requests for the company.' },
    ],
    preview: {
      kind: 'Diligence report',
      title: 'Data room findings',
      subject: 'Tallyfox · Series A · AP automation for mid-market finance teams',
      rows: [
        { label: 'ARR reconciles to revenue schedule', status: 'partial', detail: 'Deck ARR differs from the revenue schedule’s latest month annualized.', source: 'Deck slide 8 · Revenue_Schedule' },
        { label: 'Top-customer concentration', status: 'strong', detail: 'Largest customer shown as a minority share of revenue.', source: 'Customer_List' },
        { label: 'Cap table matches round summary', status: 'strong', detail: 'Share counts consistent across documents.', source: 'Cap_Table · Term_Summary' },
        { label: 'Payment-partner agreement', status: 'gap', detail: 'Referenced in board minutes; not in the data room.', source: 'Board_Minutes_Q2' },
        { label: 'Security policies', status: 'neutral', detail: 'Summary document present; detail to be reviewed by specialist.', source: 'Security_Overview' },
      ],
      gaps: ['Payment-partner agreement', 'Customer cohort data', 'Employee option pool detail'],
    },
    process: {
      heading: 'Work from your own diligence checklist',
      body: 'Use the questions and checklist your team already relies on so every data room is reviewed to the same standard. Findings feed directly into financial analysis and your investment memo.',
      configurable: ['Diligence checklist and questions', 'Required document list', 'Areas of focus by deal type'],
      next: { slug: 'financial-analysis', prompt: 'Reviewed the data room? Continue with financial analysis.' },
    },
    faqs: [
      { question: 'What does Data Room Review do?', answer: 'It helps investment teams find answers across a company’s diligence documents, flags apparent inconsistencies between documents, and lists information that is missing relative to your checklist.' },
      { question: 'What information do I need?', answer: 'The documents the company has shared and, ideally, your team’s diligence questions or checklist so the review focuses on what matters for your process.' },
      { question: 'Can I use my fund’s checklist?', answer: 'Yes. Reviewing against your own questions keeps findings consistent across deals and aligned with how your team runs diligence.' },
      { question: 'How should analysts review the results?', answer: 'Open the referenced document for each answer or flag and confirm it directly. Treat flags as prompts for follow-up, and involve legal, financial or technical specialists where the matter requires it.' },
      { question: 'How is deal data protected?', answer: 'DiligenceGPT™ uses tenant isolation and does not train on customer deal data. See the Security section and our Privacy Policy for current details.' },
    ],
    finalCta: {
      heading: 'Know what the data room says — and what it doesn’t.',
      body: 'Find answers, catch inconsistencies and build a precise follow-up list.',
    },
    related: ['financial-analysis', 'investment-memo-writer', 'pitch-deck-screener', 'valuation-comps', 'founder-research', 'competitor-analysis', 'market-research', 'portfolio-reporting', 'deal-sourcing'],
    videoId: null,
    transcript: null,
    verifiedUsageCount: null,
    illustrativeUsageCount: 380,
  },
]
